import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../utils/api";
import { formatIndianCurrency } from "../../utils/formatters";
import {
  RefreshCw,
  Download,
  TrendingUp,
  Calendar,
  AlertCircle,
  Crown,
  Star,
  Users,
  DollarSign,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const AdminEarnings = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  // Platform-wide statistics
  const [platformStats, setPlatformStats] = useState({
    totalSales: 0,
    totalCost: 0,
    totalGrossProfit: 0,
    totalNetProfit: 0,
    totalGstSales: 0,
    totalGstCost: 0,
    totalGstProfit: 0,
    totalRecords: 0,
    uniqueUsersCount: 0,
    uniqueDepositsCount: 0,
  });

  useEffect(() => {
    fetchPlatformStats();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.fromDate || filters.toDate) {
        fetchPlatformStats();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.fromDate, filters.toDate]);

  const fetchPlatformStats = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params = {};
      if (filters.fromDate) params.from_date = filters.fromDate;
      if (filters.toDate) params.to_date = filters.toDate;

      const res = await api.get("/admin/stats", { params });

      const data = res?.data || {};
      console.log("Platform stats:", data);

      if (data.success) {
        setPlatformStats(data.data || {});
      }

      setLastUpdate(new Date());
    } catch (err) {
      console.error("Failed to fetch platform stats:", err);
      toast.error("Failed to load platform statistics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchPlatformStats(true);
  };

  const handleDateChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({ fromDate: "", toDate: "" });
    fetchPlatformStats();
  };

  const exportToCSV = () => {
    try {
      const headers = [
        "Metric",
        "Value"
      ];

      const rows = [
        ["Total Sales", platformStats.totalSales.toFixed(2)],
        ["Total Cost", platformStats.totalCost.toFixed(2)],
        ["Gross Profit", platformStats.totalGrossProfit.toFixed(2)],
        ["Net Profit", platformStats.totalNetProfit.toFixed(2)],
        ["GST on Sales", platformStats.totalGstSales.toFixed(2)],
        ["GST on Cost", platformStats.totalGstCost.toFixed(2)],
        ["GST Profit", platformStats.totalGstProfit.toFixed(2)],
        ["Total Records", platformStats.totalRecords],
        ["Active Users", platformStats.uniqueUsersCount],
        ["Active Deposits", platformStats.uniqueDepositsCount],
      ];

      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `platform-earnings-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success("Report exported successfully");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Failed to export report");
    }
  };

  const processDaily = async () => {
    try {
      setRefreshing(true);
      const res = await api.post("/process-daily");
      
      if (res.data.success) {
        toast.success(`Processed ${res.data.data.processed} deposits. Total earnings: ${formatIndianCurrency(res.data.data.totalEarnings)}`);
        fetchPlatformStats();
      }
    } catch (err) {
      console.error("Failed to process daily:", err);
      toast.error("Failed to process daily earnings");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">
                Daily Sales Report (Admin)
              </h1>
              <p className="text-gray-600 mt-1">
                Platform-wide earnings overview and statistics
              </p>
            </div>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              Export CSV
            </button>
          </div>
          {lastUpdate && (
            <p className="text-xs text-gray-500 mt-2">
              Last updated:{" "}
              {lastUpdate.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>

        {/* No Data Warning */}
        {platformStats.totalRecords === 0 && !loading && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-yellow-700">
                  No Earnings Data
                </h3>
                <p className="text-sm text-gray-700 mt-1">
                  Earnings are calculated daily at midnight for active deposits.
                  You can manually trigger processing below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-600 uppercase">
                Total Sales
              </p>
              <div className="p-3 bg-primary-light rounded-lg">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
            </div>
            <p className="text-2xl font-mono font-bold text-gray-900">
              {formatIndianCurrency(platformStats.totalSales)}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              GST: {formatIndianCurrency(platformStats.totalGstSales)}
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-600 uppercase">
                Net Profit
              </p>
              <div className="p-3 bg-success-light rounded-lg">
                <TrendingUp className="w-6 h-6 text-success" />
              </div>
            </div>
            <p className="text-2xl font-mono font-bold text-gray-900">
              {formatIndianCurrency(platformStats.totalNetProfit)}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Gross: {formatIndianCurrency(platformStats.totalGrossProfit)}
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-600 uppercase">
                Active Users
              </p>
              <div className="p-3 bg-warning-light rounded-lg">
                <Users className="w-6 h-6 text-warning" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {platformStats.uniqueUsersCount}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              {platformStats.uniqueDepositsCount} active deposits
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-600 uppercase">
                Total Records
              </p>
              <div className="p-3 bg-error-light rounded-lg">
                <Calendar className="w-6 h-6 text-error" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {platformStats.totalRecords}
            </p>
            <p className="text-sm text-gray-600 mt-2">Earning entries</p>
          </div>
        </div>

        {/* Detailed Stats */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Detailed Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-2">Cost Analysis</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Total Cost:</span>
                  <span className="text-sm font-mono font-semibold">
                    {formatIndianCurrency(platformStats.totalCost)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Cost GST:</span>
                  <span className="text-sm font-mono font-semibold">
                    {formatIndianCurrency(platformStats.totalGstCost)}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-2">Profit Analysis</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Gross Profit:</span>
                  <span className="text-sm font-mono font-semibold text-emerald-600">
                    {formatIndianCurrency(platformStats.totalGrossProfit)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Net Profit:</span>
                  <span className="text-sm font-mono font-semibold text-emerald-600">
                    {formatIndianCurrency(platformStats.totalNetProfit)}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-2">GST Summary</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Sales GST:</span>
                  <span className="text-sm font-mono font-semibold">
                    {formatIndianCurrency(platformStats.totalGstSales)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Profit GST:</span>
                  <span className="text-sm font-mono font-semibold">
                    {formatIndianCurrency(platformStats.totalGstProfit)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Date Filter</h3>
            </div>
            <button
              onClick={resetFilters}
              className="text-sm text-primary font-medium hover:underline"
            >
              Reset
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                From Date
              </label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleDateChange("fromDate", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                To Date
              </label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => handleDateChange("toDate", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <RefreshCw
              className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}
            />
            {refreshing ? "Refreshing..." : "Refresh Data"}
          </button>

          <button
            onClick={processDaily}
            disabled={refreshing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <TrendingUp className="w-5 h-5" />
            Process Daily Earnings
          </button>
        </div>

        {/* Info Card */}
        {/* <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 mb-3">
            About Daily Earnings
          </h4>
          <ul className="text-sm text-gray-600 space-y-2">
            <li>
              • Earnings are automatically calculated daily at midnight for all
              active deposits
            </li>
            <li>• Cost = Capital ÷ 30 days</li>
            <li>
              • <Star className="w-3 h-3 inline text-blue-600" /> Prime plan: 4%
              margin | <Crown className="w-3 h-3 inline text-purple-600" />{" "}
              Premium plan: 8% margin
            </li>
            <li>• GST @ 5% is applied on both cost and sales</li>
            <li>• Net Profit = Gross Profit - Profit GST</li>
            <li className="font-semibold text-primary">
              • Use "Process Daily Earnings" button to manually trigger
              processing
            </li>
          </ul>
        </div> */}
      </div>
    </Layout>
  );
};

export default AdminEarnings;